import { useState } from 'react';
import { Outlet, NavLink, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { LayoutDashboard, Megaphone, BarChart3, ArrowLeft, LogOut, ShoppingBag, Tag, Bell, Star, Menu, X, Repeat, Users } from 'lucide-react';

const navItems = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/campaigns', label: 'Campagne', icon: Megaphone },
  { to: '/admin/discount-codes', label: 'Codici Sconto', icon: Tag },
  { to: '/admin/push', label: 'Push', icon: Bell },
  { to: '/admin/reviews', label: 'Recensioni', icon: Star },
  { to: '/admin/orders', label: 'Ordini', icon: ShoppingBag, adminOnly: true },
  { to: '/admin/subscriptions', label: 'Abbonamenti', icon: Repeat, adminOnly: true },
  { to: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
  { to: '/admin/users', label: 'Utenti', icon: Users, adminOnly: true },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const closeDrawer = () => setDrawerOpen(false);

  const Sidebar = ({ onItemClick }) => (
    <>
      <Link to="/" className="font-heading text-xl tracking-tighter mb-8 block" data-testid="admin-brand-link"
        style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-primary)', textDecoration: 'none' }}>
        Quintessenza
      </Link>
      <nav className="flex-1 space-y-1">
        {navItems.filter(item => !item.adminOnly || user?.role === 'admin').map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} onClick={onItemClick} data-testid={`admin-nav-${label.toLowerCase().replace(/ /g, '-')}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-body transition-colors ${isActive ? 'font-medium' : ''}`
            }
            style={({ isActive }) => ({
              fontFamily: 'var(--font-body)',
              backgroundColor: isActive ? 'var(--bg-primary)' : 'transparent',
              color: isActive ? 'var(--accent)' : 'var(--text-secondary)',
            })}>
            <Icon className="w-4 h-4" />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t pt-4 space-y-2 mt-4" style={{ borderColor: 'var(--border-light)' }}>
        <Link to="/" onClick={onItemClick} className="flex items-center gap-2 px-3 py-2 text-xs font-body transition-colors hover:opacity-70"
          style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-body)', textDecoration: 'none' }}>
          <ArrowLeft className="w-3.5 h-3.5" /> Torna al Negozio
        </Link>
        <button onClick={handleLogout} data-testid="admin-logout-btn"
          className="flex items-center gap-2 px-3 py-2 text-xs font-body transition-colors hover:opacity-70 w-full text-left"
          style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>
          <LogOut className="w-3.5 h-3.5" /> Esci
        </button>
        {user && (
          <p className="px-3 text-xs font-body" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>
            {user.name}
          </p>
        )}
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--bg-admin)' }}>
      {/* Desktop Sidebar */}
      <aside data-testid="admin-sidebar" className="admin-sidebar w-60 flex-shrink-0 flex-col p-5 hidden md:flex">
        <Sidebar />
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 border-b p-3 flex items-center justify-between"
        style={{ backgroundColor: 'var(--bg-white)', borderColor: 'var(--border-light)' }}>
        <Link to="/" className="font-heading text-lg tracking-tighter" style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-primary)', textDecoration: 'none' }}>
          Quintessenza
        </Link>
        <button onClick={() => setDrawerOpen(true)} data-testid="open-admin-drawer-btn" aria-label="Apri menu"
          className="p-2 rounded-sm" style={{ color: 'var(--text-primary)' }}>
          <Menu className="w-5 h-5" />
        </button>
      </div>

      {/* Mobile Drawer */}
      {drawerOpen && (
        <>
          <div onClick={closeDrawer} className="md:hidden fixed inset-0 z-[60]"
            style={{ backgroundColor: 'rgba(0,0,0,0.4)' }} data-testid="admin-drawer-overlay" />
          <aside className="md:hidden fixed top-0 left-0 bottom-0 z-[70] w-72 max-w-[85vw] p-5 flex flex-col animate-slide-in-left overflow-y-auto"
            style={{ backgroundColor: 'var(--bg-white)' }} data-testid="admin-drawer">
            <button onClick={closeDrawer} aria-label="Chiudi menu" data-testid="close-admin-drawer-btn"
              className="absolute top-3 right-3 p-1.5" style={{ color: 'var(--text-muted)' }}>
              <X className="w-5 h-5" />
            </button>
            <Sidebar onItemClick={closeDrawer} />
          </aside>
        </>
      )}

      {/* Content */}
      <main className="flex-1 overflow-auto md:p-8 p-4 pt-20 md:pt-8" data-testid="admin-content">
        <Outlet />
      </main>
    </div>
  );
}
